import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DropdownIcon from 'assets/icons/Dropdown';
import BaseButton from 'components/ui/BaseButton';
import { KEY_BOARD, OVERLAY_Z_INDEX_CLASS } from 'constants/general';
import { MULTI_SELECT } from 'constants/multiSelect';
import { t } from 'services/i18n.service';
import COLOR from 'constants/color';

export interface MultiSelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

type MultiSelectSize = 'xs' | 'sm' | 'md' | 'lg';

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: Array<string | number>;
  onChange: (next: Array<string | number>) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: MultiSelectSize;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Notified on every search keystroke � for server-driven option lists. */
  onSearchQueryChange?: (query: string) => void;
  /** Fires when the dropdown opens or closes (e.g. lazy-load options). */
  onOpenChange?: (isOpen: boolean) => void;
  /** Disable to keep server-filtered options untouched (default true). */
  shouldFilterLocally?: boolean;
  /** Message when no options match (defaults to the generic "no options"). */
  noOptionsMessage?: string;
  showSelectAll?: boolean;
  visibleSelectedCount?: number;
  actionButtons?: MultiSelectAction[];
  btnClassName?: string;
  /** Optional confirm in the dropdown footer (e.g. enrolment pickers). */
  confirmLabel?: string;
  onConfirm?: () => boolean | void | Promise<boolean | void>;
  isConfirmLoading?: boolean;
  isConfirmDisabled?: boolean;
}

export interface MultiSelectAction {
  id: string;
  label: string;
  getValues: (args: {
    options: MultiSelectOption[];
    currentValue: Array<string | number>;
  }) => Array<string | number>;
}

const DEFAULT_PLACEHOLDER = t('ui.multiselect.placeholder');

const containerSizeClasses: Record<MultiSelectSize, string> = {
  xs: 'w-full min-w-0 sm:w-48 sm:min-w-48',
  sm: 'w-full min-w-0 sm:w-56 sm:min-w-56',
  md: 'w-full min-w-0 sm:w-64 sm:min-w-64',
  lg: 'w-full min-w-0 sm:w-72 sm:min-w-72'
};

const optionSizeClasses: Record<MultiSelectSize, string> = {
  xs: '~text-[0.5rem]/[0.625rem] ~py-0.5/1 ~px-2/2.5',
  sm: '~text-[0.625rem]/xs ~py-1/1.5 ~px-2.5/3',
  md: '~text-xs/sm ~py-1.5/2 ~px-3/4',
  lg: '~text-sm/base ~py-2/2.5 ~px-4/5'
};

const actionSizeClasses: Record<MultiSelectSize, string> = {
  xs: '~text-[0.5rem]/[0.625rem] ~py-[0.375rem]/0.5 ~px-1.5/2',
  sm: '~text-[0.625rem]/xs ~py-0.5/1 ~px-2/2.5',
  md: '~text-xs/sm ~py-1/1.5 ~px-2.5/3',
  lg: '~text-sm/base ~py-1.5/2 ~px-3/4'
};

const searchSizeClasses: Record<MultiSelectSize, string> = {
  xs: '~text-[0.5rem]/[0.625rem] ~px-1.5/2 ~py-0.5/1',
  sm: '~text-[0.625rem]/xs ~px-2/2.5 ~py-0.5/1',
  md: '~text-xs/sm ~px-2/2.5 ~py-1/1.5',
  lg: '~text-sm/base ~px-2.5/3 ~py-1/1.5'
};

const iconSizeClasses: Record<MultiSelectSize, string> = {
  xs: '~w-2.5/3 h-auto',
  sm: '~w-3/4 h-auto',
  md: '~w-3.5/5 h-auto',
  lg: '~w-4/6 h-auto'
};

const iconWrapperSizeClasses: Record<MultiSelectSize, string> = {
  xs: 'ml-0.5',
  sm: 'ml-1',
  md: 'ml-1.5',
  lg: 'ml-2'
};

type DropdownPlacement = 'top' | 'bottom';

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  placement: DropdownPlacement;
}

const getSummaryParts = (
  selectedOptions: MultiSelectOption[],
  placeholder: string,
  visibleSelectedCount: number
): { labelsText: string; remainingCount: number } => {
  if (selectedOptions.length === 0) {
    return { labelsText: placeholder, remainingCount: 0 };
  }

  if (selectedOptions.length <= visibleSelectedCount) {
    return {
      labelsText: selectedOptions.map((opt) => opt.label).join(', '),
      remainingCount: 0
    };
  }

  const visible = selectedOptions.slice(0, visibleSelectedCount);
  const remainingCount = selectedOptions.length - visibleSelectedCount;
  return {
    labelsText: visible.map((opt) => opt.label).join(', '),
    remainingCount
  };
};

const filterOptions = (
  options: MultiSelectOption[],
  query: string
): MultiSelectOption[] => {
  if (!query.trim()) {
    return options;
  }

  const lowered = query.toLowerCase();
  return options.filter((opt) => opt.label.toLowerCase().includes(lowered));
};

const toggleValue = (
  current: Array<string | number>,
  nextValue: string | number
) => {
  const exists = current.some((item) => String(item) === String(nextValue));
  if (exists) {
    return current.filter((item) => String(item) !== String(nextValue));
  }
  return [...current, nextValue];
};

const areAllSelectableChecked = (
  options: MultiSelectOption[],
  value: Array<string | number>
) => {
  const selectableValues = options
    .filter((opt) => !opt.disabled)
    .map((opt) => String(opt.value));

  if (selectableValues.length === 0) {
    return false;
  }

  return selectableValues.every((val) =>
    value.some((selected) => String(selected) === val)
  );
};

const MultiSelect = ({
  options,
  value,
  onChange,
  placeholder = DEFAULT_PLACEHOLDER,
  disabled,
  className,
  size = 'md',
  searchable = true,
  searchPlaceholder = t('ui.multiselect.search_placeholder'),
  onSearchQueryChange,
  onOpenChange,
  shouldFilterLocally = true,
  noOptionsMessage,
  showSelectAll = true,
  visibleSelectedCount = MULTI_SELECT.VISIBLE_SELECTED_COUNT,
  actionButtons,
  btnClassName,
  confirmLabel,
  onConfirm,
  isConfirmLoading = false,
  isConfirmDisabled = false
}: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<DropdownPosition>({
    top: 0,
    left: 0,
    width: 0,
    placement: 'bottom'
  });

  const filteredOptions = useMemo(
    () => (shouldFilterLocally ? filterOptions(options, searchQuery) : options),
    [options, searchQuery, shouldFilterLocally]
  );

  const selectedOptions = useMemo(
    () =>
      options.filter((opt) =>
        value.some((item) => String(item) === String(opt.value))
      ),
    [options, value]
  );

  const summaryParts = useMemo(
    () => getSummaryParts(selectedOptions, placeholder, visibleSelectedCount),
    [selectedOptions, placeholder, visibleSelectedCount]
  );

  const getWidthValue = (width?: string | number) => {
    if (!width) return undefined;
    return typeof width === 'number' ? `${width}px` : width;
  };

  const calculatePosition = useCallback((): DropdownPosition => {
    if (!buttonRef.current) {
      return { top: 0, left: 0, width: 0, placement: 'bottom' };
    }

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    let actualDropdownHeight = 0;
    if (dropdownRef.current) {
      actualDropdownHeight = dropdownRef.current.getBoundingClientRect().height;
    }

    const totalOptions = filteredOptions.length;
    const confirmFooterHeightPx =
      confirmLabel && onConfirm ? MULTI_SELECT.CONFIRM_FOOTER_HEIGHT_PX : 0;
    const estimatedDropdownHeight =
      Math.min(totalOptions * 40 + 8, 240) + confirmFooterHeightPx;
    const dropdownHeight = actualDropdownHeight || estimatedDropdownHeight;

    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    const shouldPlaceAbove =
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    const placement: DropdownPlacement = shouldPlaceAbove ? 'top' : 'bottom';

    let top: number;
    if (placement === 'bottom') {
      top = buttonRect.bottom + scrollY + 4;
    } else {
      top = buttonRect.top + scrollY - dropdownHeight - 4;
    }

    const padding = 8;
    let left = buttonRect.left + scrollX;

    if (left + buttonRect.width > viewportWidth - padding) {
      left = viewportWidth - buttonRect.width - padding + scrollX;
    }

    if (left < padding + scrollX) {
      left = padding + scrollX;
    }

    return {
      top,
      left,
      width:
        confirmLabel && onConfirm
          ? Math.max(
              buttonRect.width,
              MULTI_SELECT.CONFIRM_DROPDOWN_MIN_WIDTH_PX
            )
          : buttonRect.width,
      placement
    };
  }, [filteredOptions, confirmLabel, onConfirm]);

  const handleToggleOpen = () => {
    if (disabled) return;
    setIsOpen((prev) => {
      const nextIsOpen = !prev;
      onOpenChange?.(nextIsOpen);
      return nextIsOpen;
    });
  };

  const handleButtonKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (disabled) return;
    if (
      event.key === KEY_BOARD.ENTER.KEY ||
      event.key === KEY_BOARD.SPACE.KEY
    ) {
      event.preventDefault();
      handleToggleOpen();
    }
    if (event.key === KEY_BOARD.ESCAPE.KEY) {
      setIsOpen(false);
      onOpenChange?.(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;

      const clickedInsideTrigger =
        buttonRef.current && buttonRef.current.contains(target);
      const clickedInsideDropdown =
        dropdownRef.current && dropdownRef.current.contains(target);
      const clickedInsideContainer =
        containerRef.current && containerRef.current.contains(target);

      if (
        clickedInsideTrigger ||
        clickedInsideDropdown ||
        clickedInsideContainer
      ) {
        return;
      }
      setIsOpen(false);
      onOpenChange?.(false);
      setSearchQuery('');
      onSearchQueryChange?.('');
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, onSearchQueryChange]);

  useEffect(() => {
    if (!isOpen) return;

    const update = () => {
      const newPosition = calculatePosition();
      setPosition(newPosition);
    };

    update();
    const timeoutId = window.setTimeout(update, 0);

    const handleResize = () => update();
    const handleScroll = () => update();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, calculatePosition]);

  const handleOptionToggle = (option: MultiSelectOption) => {
    if (option.disabled) return;
    const next = toggleValue(value, option.value);
    onChange(next);
  };

  const handleSelectAllToggle = () => {
    const selectableValues = filteredOptions
      .filter((opt) => !opt.disabled)
      .map((opt) => opt.value);

    if (areAllSelectableChecked(filteredOptions, value)) {
      const remainingValues = value.filter(
        (selected) =>
          !selectableValues.some(
            (optValue) => String(optValue) === String(selected)
          )
      );
      onChange(remainingValues);
      return;
    }

    const mergedValues = [
      ...value,
      ...selectableValues.filter(
        (optValue) =>
          !value.some((selected) => String(selected) === String(optValue))
      )
    ];
    onChange(mergedValues);
  };

  const handleClear = () => {
    if (!value.length) {
      return;
    }
    onChange([]);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    onOpenChange?.(false);
    setSearchQuery('');
    onSearchQueryChange?.('');
  };

  const handleConfirmClick = async () => {
    if (!onConfirm || isConfirmDisabled || isConfirmLoading) {
      return;
    }
    const result = await onConfirm();
    if (result === false) {
      return;
    }
    closeDropdown();
  };

  const isAllChecked = areAllSelectableChecked(filteredOptions, value);
  const shouldShowConfirm = !!confirmLabel && !!onConfirm;

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      role='listbox'
      className={`fixed ${OVERLAY_Z_INDEX_CLASS.POPOVER} flex flex-col ~max-h-60/72 overflow-hidden rounded-md shadow-md md:shadow-lg bg-white dark:bg-secondary-dark-200 border border-gray-200 dark:border-secondary-dark-400`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: getWidthValue(position.width),
        transformOrigin:
          position.placement === 'top' ? 'bottom center' : 'top center'
      }}>
      {searchable && (
        <div className='p-2 border-b border-gray-200 dark:border-secondary-dark-400 shrink-0 bg-white dark:bg-secondary-dark-200'>
          <input
            type='text'
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              onSearchQueryChange?.(event.target.value);
            }}
            placeholder={searchPlaceholder}
            className={`w-full rounded border border-gray-300 dark:border-secondary-dark-500 bg-white dark:bg-secondary-dark-300 text-gray-900 dark:text-secondary-200 focus:outline-none focus:ring-1 focus:ring-primary-500 ${searchSizeClasses[size]}`}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}

      <div className='~py-1/2 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-secondary-dark-500 scrollbar-track-transparent dark:scrollbar-track-transparent'>
        {showSelectAll && filteredOptions.length > 0 && (
          <div className='flex flex-wrap items-center ~gap-1/2 px-2 pb-1'>
            <button
              disabled={isAllChecked || filteredOptions.length === 0}
              type='button'
              onClick={handleSelectAllToggle}
              className={` text-primary-dark-50 dark:text-secondary-200 hover:bg-gray-100 dark:hover:bg-secondary-dark-300 rounded ${actionSizeClasses[size]}`}>
              <span>{t('ui.multiselect.select_all')}</span>
            </button>
            <button
              disabled={value.length === 0}
              type='button'
              onClick={handleClear}
              className={` text-primary-dark-50 dark:text-secondary-200 hover:bg-gray-100 dark:hover:bg-secondary-dark-300 rounded ${actionSizeClasses[size]}`}>
              <span>{t('ui.multiselect.clear')}</span>
            </button>
            {actionButtons?.map((action) => (
              <button
                key={action.id}
                type='button'
                onClick={() => {
                  const next = action.getValues({
                    options: filteredOptions,
                    currentValue: value
                  });
                  onChange(next);
                }}
                className={` text-primary-dark-50 dark:text-secondary-200 hover:bg-gray-100 dark:hover:bg-secondary-dark-300 rounded ${actionSizeClasses[size]}`}>
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        )}

        {filteredOptions.length === 0 && (
          <div
            className={`${optionSizeClasses[size]} text-gray-500 dark:text-secondary-dark-700 text-center`}>
            {noOptionsMessage ?? t('ui.multiselect.no_options')}
          </div>
        )}

        {filteredOptions.map((option) => {
          const isChecked = value.some(
            (item) => String(item) === String(option.value)
          );

          return (
            <button
              key={String(option.value)}
              type='button'
              onClick={() => handleOptionToggle(option)}
              disabled={option.disabled}
              className={`w-full flex items-center gap-2 text-left ${optionSizeClasses[size]} ${
                option.disabled
                  ? 'text-gray-400 dark:text-secondary-600 cursor-not-allowed'
                  : 'text-primary-dark-50 dark:text-secondary-100 hover:bg-gray-100 dark:hover:bg-secondary-dark-300 cursor-pointer'
              }`}>
              <input
                type='checkbox'
                readOnly
                checked={isChecked}
                className='w-3.5 h-3.5 flex-shrink-0'
              />
              <span className='truncate'>{option.label}</span>
            </button>
          );
        })}
      </div>

      {shouldShowConfirm && (
        <div className='shrink-0 border-t border-gray-200 dark:border-secondary-dark-400 ~p-1.5/2 bg-white dark:bg-secondary-dark-200'>
          <BaseButton
            title={confirmLabel}
            size={size === 'lg' ? 'md' : 'sm'}
            onClick={handleConfirmClick}
            isLoading={isConfirmLoading}
            disabled={isConfirmDisabled || isConfirmLoading}
            additionalStyles='w-full'
          />
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      <div
        ref={containerRef}
        className={`inline-block max-w-full text-left ${containerSizeClasses[size]} ${className ?? ''}`}>
        <button
          ref={buttonRef}
          type='button'
          disabled={disabled}
          onClick={handleToggleOpen}
          onKeyDown={handleButtonKeyDown}
          className={`flex h-fit w-full min-w-0 items-center justify-between gap-1.5 rounded-md border border-primary-500 bg-primary-50 text-primary-900 shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-secondary-dark-400 dark:bg-secondary-dark-200 dark:text-white ${optionSizeClasses[size]} ${btnClassName}`}>
          <span className='flex min-w-0 flex-1 items-center gap-1 text-left'>
            <span className='min-w-0 truncate'>{summaryParts.labelsText}</span>
            {summaryParts.remainingCount > 0 && (
              <span className='flex-shrink-0 text-xs text-primary-900 dark:text-white'>
                +{summaryParts.remainingCount}
              </span>
            )}
          </span>
          <span
            aria-hidden
            className={`${iconWrapperSizeClasses[size]} transition-transform duration-200 ${
              isOpen ? 'rotate-180' : 'rotate-0'
            }`}>
            <DropdownIcon
              styles={iconSizeClasses[size]}
              fill={COLOR.PRIMARY.LIGHT_900}
            />
          </span>
        </button>
      </div>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
};

export default MultiSelect;
